import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, RefreshCw, AlertCircle, CheckCircle2, ServerCrash } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { adminApi, formatError } from "../lib/api";

/**
 * AdminHealth — operator-only dashboard surfaced via Profile.
 *
 * Backend enforces the actual auth gate (`_ADMIN_EMAILS` allow-list); this
 * page just calls /api/admin/health. Non-admin users hitting this route
 * directly will get a 403 and we render a friendly "Not authorized" empty
 * state — no leaked numbers.
 *
 * Auto-refreshes every 30s so the heartbeat / last-tick countdown stays
 * fresh. Pauses when the tab is hidden so we don't beat up Mongo.
 */
const Card = ({ title, children, testid }) => (
  <div className="tup-card p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8A93A6] mb-2">
      {title}
    </div>
    {children}
  </div>
);

const Stat = ({ label, value, hint }) => (
  <div>
    <div className="text-[10px] text-[#8A93A6] uppercase tracking-wider">{label}</div>
    <div className="font-heading text-2xl font-bold text-[#1F5566] mt-0.5">{value}</div>
    {hint && <div className="text-[10px] text-[#8A93A6] mt-0.5">{hint}</div>}
  </div>
);

const AdminHealth = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const d = await adminApi.health();
      setData(d);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Auto-refresh every 30s, but only while the tab is visible.
    const tick = () => { if (!document.hidden) load(); };
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [load]);

  const schedHealthy =
    data?.background?.scheduler_seconds_since_last_tick != null &&
    data.background.scheduler_seconds_since_last_tick < 20 * 60;

  return (
    <div className="px-5 pt-6 pb-8" data-testid="admin-health-page">
      <button
        onClick={() => navigate(-1)}
        data-testid="admin-back-btn"
        className="text-sm text-[#4F5D75] hover:text-[#2D3142] flex items-center gap-2 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#8A93A6]">
              Operator
            </div>
            <h1 className="font-heading text-3xl font-bold text-[#2D3142] mt-1">
              App health
            </h1>
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh-btn"
            aria-label="Refresh"
            className="w-10 h-10 rounded-full bg-[#DCEDF0] text-[#1F5566] flex items-center justify-center hover:bg-[#C4DFE3]"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
        <div className="text-[11px] text-[#8A93A6] mt-1">
          Signed in as <span className="font-semibold">{user?.email}</span>. Auto-refreshes every 30s.
        </div>
      </motion.div>

      {error && (
        <div
          data-testid="admin-error"
          className="mt-5 tup-card p-4 border-2 border-[#E07A5F] bg-[#FBEAE5] flex items-start gap-3"
        >
          <ServerCrash className="w-5 h-5 text-[#9C3E1D] shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-[#9C3E1D]">Couldn't load health data</div>
            <div className="text-xs text-[#4F5D75] mt-1">{error}</div>
            <div className="text-[10px] text-[#8A93A6] mt-2">
              If this says "Not authorized", your email isn't on the admin allow-list.
            </div>
          </div>
        </div>
      )}

      {data && (
        <div className="mt-5 space-y-3">
          <Card title="Users" testid="card-users">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Total" value={data.users.total} />
              <Stat label="Premium" value={data.users.premium} hint={`${data.users.total ? Math.round((data.users.premium / data.users.total) * 100) : 0}% of base`} />
              <Stat label="New (24h)" value={data.users.new_24h} />
              <Stat label="New (7d)" value={data.users.new_7d} />
            </div>
          </Card>

          <Card title="Inventory" testid="card-inventory">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Households" value={data.households.total} />
              <Stat label="Containers (total)" value={data.containers.total} />
              <Stat label="Containers (active)" value={data.containers.active} />
              <Stat
                label="Avg per household"
                value={data.households.total ? Math.round(data.containers.total / data.households.total) : 0}
              />
            </div>
          </Card>

          <Card title="Payments" testid="card-payments">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Sessions started" value={data.payments.transactions_total} />
              <Stat label="Paid" value={data.payments.paid_total} />
            </div>
            <div className="mt-3 text-[11px] text-[#8A93A6]">
              Stripe mode:{" "}
              <span
                className={`font-semibold ${
                  data.stripe_mode === "live"
                    ? "text-[#3A7A3A]"
                    : data.stripe_mode === "sandbox"
                    ? "text-[#B27300]"
                    : "text-[#9C3E1D]"
                }`}
                data-testid="admin-stripe-mode"
              >
                {data.stripe_mode}
              </span>
            </div>
          </Card>

          <Card title="Background scheduler" testid="card-scheduler">
            <div className="flex items-center gap-2">
              {schedHealthy ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-[#3A7A3A]" />
                  <span className="text-sm font-semibold text-[#3A7A3A]">Healthy</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 text-[#9C3E1D]" />
                  <span className="text-sm font-semibold text-[#9C3E1D]">
                    {data.background.scheduler_last_tick ? "Stale" : "Never run"}
                  </span>
                </>
              )}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <Stat
                label="Last tick"
                value={
                  data.background.scheduler_seconds_since_last_tick != null
                    ? `${data.background.scheduler_seconds_since_last_tick}s ago`
                    : "—"
                }
              />
              <Stat
                label="Last duration"
                value={
                  data.background.scheduler_last_duration_ms != null
                    ? `${data.background.scheduler_last_duration_ms} ms`
                    : "—"
                }
              />
            </div>
            {data.background.scheduler_last_error && (
              <div className="mt-3 text-[11px] text-[#9C3E1D] bg-[#FBEAE5] rounded-lg p-2 font-mono break-words">
                {data.background.scheduler_last_error}
              </div>
            )}
          </Card>

          <Card title="Dedup table" testid="card-dedup">
            <Stat
              label="email_notifications_sent rows"
              value={data.dedup.email_notifications_sent_rows.toLocaleString()}
              hint={
                data.users.total > 0 && data.dedup.email_notifications_sent_rows > data.users.total * 100
                  ? "⚠️ Growing fast — consider a TTL index"
                  : "Healthy"
              }
            />
          </Card>

          <div className="text-[10px] text-[#8A93A6] text-center pt-2">
            Snapshot took {data.query_ms}ms · {new Date(data.as_of).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHealth;
